import React, { useState, useRef, useEffect } from 'react';
import imageCompression from 'browser-image-compression';
import Parse from 'parse';
import { FaPlayCircle } from 'react-icons/fa';
import {
  usePhotoDedupe,
  hashFile,
  checkPhotoHash,
  registerPhotoHash,
  duplicateMessage,
} from '../../photoDedupe';

/**
 * Reusable file upload component with compression and Back4App upload
 * @param {Object} props
 * @param {Array} props.value - Array of uploaded image/video URLs
 * @param {Function} props.onChange - Callback when files change, receives array of URLs
 * @param {Function} props.onUploadStatusChange - Callback when upload status changes, receives boolean (isUploading)
 * @param {boolean} props.required - Whether field is required
 * @param {string} props.label - Label text
 * @param {boolean} props.hasError - Whether to show error styling
 * @param {string} props.errorMessage - Error message to display
 * @param {boolean} props.multiple - Allow multiple file selection
 * @param {boolean} props.allowVideo - Whether to allow video uploads (default: true)
 * @param {string} props.dedupeField - Which slot this is, recorded against the photo's
 *   hash so a later duplicate can name the field it came from. Only has an effect
 *   inside a PhotoDedupeContext provider.
 */
export default function FileUpload({
  value = [],
  onChange,
  onUploadStatusChange,
  required = false,
  label = '',
  hasError = false,
  errorMessage = '',
  multiple = true,
  allowVideo = true, // Default to true - allow videos by default
  dedupeField = '',
}) {
  // Track file state: { fileId: { previewUrl, uploadedUrl, isUploading, videoFrameLoaded } }
  const [fileStates, setFileStates] = useState({});
  const fileInputRef = useRef(null);
  const fileIdCounter = useRef(0);
  
  // Track if any files are currently uploading
  const [isUploading, setIsUploading] = useState(false);

  // Present only inside the add-unit form, which is what scopes the duplicate
  // check to field maintenance photos.
  const dedupeContext = usePhotoDedupe();
  const [duplicateError, setDuplicateError] = useState(null);
  
  // Update parent when upload status changes
  useEffect(() => {
    const hasUploadingFiles = Object.values(fileStates).some(state => state.isUploading);
    setIsUploading(hasUploadingFiles);
    if (onUploadStatusChange) {
      onUploadStatusChange(hasUploadingFiles);
    }
  }, [fileStates, onUploadStatusChange]);

  // Initialize Parse
  const initializeParse = () => {
    if (!Parse.applicationId) {
      // Back4App credentials
      const APP_ID = process.env.REACT_APP_B4A_APP_ID || 'JPeNcGnGVRxgfjR3k0WTs3fbP6Vu4AagMGFXanwd';
      // Using Master Key for now (NOTE: This is a security risk - Master Key should only be used server-side)
      // TODO: Switch back to JavaScript Key and enable public file uploads in Back4App settings
      const JS_KEY = process.env.REACT_APP_B4A_MASTER_KEY || process.env.REACT_APP_B4A_JAVASCRIPT_KEY || 'Z2fZSh0JCZQQpBpR4VTjJya7xF0qNSnqhc0oyLSD';
      const SERVER_URL = process.env.REACT_APP_B4A_SERVER_URL || 'https://parseapi.back4app.com';
      
      Parse.initialize(APP_ID, JS_KEY);
      Parse.serverURL = SERVER_URL;
    }
  };

  const isHeicFile = (file) => {
    const type = (file?.type || '').toLowerCase();
    const name = (file?.name || '').toLowerCase();
    return type.includes('heic') || type.includes('heif') || name.endsWith('.heic') || name.endsWith('.heif');
  };

  const convertHeicToJpeg = async (file) => {
    try {
      const heic2any = (await import('heic2any')).default;
      const converted = await heic2any({
        blob: file,
        toType: 'image/jpeg',
        quality: 0.9,
      });
      const jpegBlob = Array.isArray(converted) ? converted[0] : converted;
      const newName = `${file.name.replace(/\.[^/.]+$/, '') || 'image'}.jpg`;
      const jpegFile = new File([jpegBlob], newName, { type: 'image/jpeg' });
      console.log('[FileUpload] Converted HEIC to JPEG:', file.name, '->', jpegFile.name);
      return jpegFile;
    } catch (err) {
      console.error('[FileUpload] Failed to convert HEIC, using original file:', err);
      return file;
    }
  };

  const compressImage = async (file, overrides = {}) => {
    // Only compress images, not videos
    if (!file.type.startsWith('image/')) {
      return file;
    }

    const options = {
      maxSizeMB: 0.25, // tighter target
      maxWidthOrHeight: 1400, // slightly smaller to reduce size further
      useWebWorker: true,
      fileType: 'image/jpeg', // Convert to JPEG for better compression
      initialQuality: 0.6, // a bit more aggressive
      // Try multiple compression passes if needed to reach target size
      alwaysKeepResolution: false, // Allow resizing if needed
      ...overrides,
    };

    try {
      const originalSizeMB = (file.size / 1024 / 1024).toFixed(2);
      const compressedFile = await imageCompression(file, options);
      const compressedSizeMB = (compressedFile.size / 1024 / 1024).toFixed(2);
      const compressionRatio = ((1 - compressedFile.size / file.size) * 100).toFixed(1);
      
      console.log(`[FileUpload] Compressed ${file.name}: ${originalSizeMB}MB -> ${compressedSizeMB}MB (${compressionRatio}% reduction)`);
      
      // If still too large, try one more pass with more aggressive settings
      if (compressedFile.size > options.maxSizeMB * 1024 * 1024) {
        console.log(`[FileUpload] File still large (${compressedSizeMB}MB), applying additional compression...`);
        const secondPassOptions = {
          ...options,
          maxSizeMB: Math.min(options.maxSizeMB, 0.18),
          initialQuality: Math.min(options.initialQuality, 0.5),
          maxWidthOrHeight: Math.min(options.maxWidthOrHeight, 1200),
        };
        const secondPass = await imageCompression(compressedFile, secondPassOptions);
        const finalSizeMB = (secondPass.size / 1024 / 1024).toFixed(2);
        console.log(`[FileUpload] Second pass: ${compressedSizeMB}MB -> ${finalSizeMB}MB`);
        return secondPass;
      }
      
      return compressedFile;
    } catch (error) {
      console.error('[FileUpload] Error compressing image:', error);
      return file; // Return original if compression fails
    }
  };

  const compressVideo = async (file) => {
    // Only compress videos
    if (!file.type.startsWith('video/')) {
      return file;
    }

    const MAX_VIDEO_SIZE_MB = 50; // Maximum recommended video size (50MB)
    const WARNING_SIZE_MB = 20; // Size at which to warn user (20MB)
    
    const fileSizeMB = file.size / 1024 / 1024;
    
    // Log video info
    console.log(`[FileUpload] Video ${file.name}: ${fileSizeMB.toFixed(2)}MB, type: ${file.type}`);
    
    // For now, we'll upload videos as-is
    // Video compression in the browser is complex and resource-intensive
    // Future enhancement: Could integrate ffmpeg.wasm for more robust compression
    // For now, we rely on the user's device to provide reasonably sized videos
    
    if (fileSizeMB > MAX_VIDEO_SIZE_MB) {
      console.warn(`[FileUpload] Video is large (${fileSizeMB.toFixed(2)}MB). Consider compressing before upload.`);
      // Could show a user warning here if needed
    }
    
    // Return video as-is (no compression for now)
    // Note: For production, consider:
    // 1. Server-side compression
    // 2. Using ffmpeg.wasm (adds ~20MB to bundle)
    // 3. Client-side compression using MediaRecorder (complex, browser-dependent)
    return file;
  };

  // Sanitize filename for Back4App (remove invalid characters)
  const sanitizeFilename = (filename) => {
    // Remove or replace invalid characters
    // Back4App doesn't like: spaces, special characters, unicode characters
    // Keep only: letters, numbers, dots, hyphens, underscores
    let sanitized = filename
      .replace(/[^a-zA-Z0-9._-]/g, '_') // Replace invalid chars with underscore
      .replace(/_{2,}/g, '_') // Replace multiple underscores with single
      .replace(/^_+|_+$/g, ''); // Remove leading/trailing underscores
    
    // Ensure it has a valid extension
    const extension = filename.split('.').pop()?.toLowerCase() || 'jpg';
    const nameWithoutExt = sanitized.split('.').slice(0, -1).join('.') || sanitized;
    
    // Create a safe filename with timestamp to ensure uniqueness
    const timestamp = Date.now();
    const random = Math.random().toString(36).substring(2, 8);
    return `${nameWithoutExt}_${timestamp}_${random}.${extension}`;
  };

  const uploadToBack4App = async (file, fileId) => {
    try {
      initializeParse();
      
      const APP_ID = process.env.REACT_APP_B4A_APP_ID || 'JPeNcGnGVRxgfjR3k0WTs3fbP6Vu4AagMGFXanwd';
      const MASTER_KEY = process.env.REACT_APP_B4A_MASTER_KEY || 'Z2fZSh0JCZQQpBpR4VTjJya7xF0qNSnqhc0oyLSD';
      const SERVER_URL = process.env.REACT_APP_B4A_SERVER_URL || 'https://parseapi.back4app.com';
      
      // Sanitize filename to remove invalid characters
      const sanitizedFilename = sanitizeFilename(file.name);
      console.log(`[FileUpload] Original filename: ${file.name}, Sanitized: ${sanitizedFilename}`);
      
      // Upload using REST API with Master Key in headers
      // Parse expects the file as binary data, not base64
      const response = await fetch(`${SERVER_URL}/files/${encodeURIComponent(sanitizedFilename)}`, {
        method: 'POST',
        headers: {
          'X-Parse-Application-Id': APP_ID,
          'X-Parse-Master-Key': MASTER_KEY,
          'Content-Type': file.type || 'image/jpeg',
        },
        body: file, // Send file directly as binary
      });
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: 'Unknown error' }));
        console.error('[FileUpload] Upload error response:', errorData);
        throw new Error(errorData.error || `Upload failed with status ${response.status}`);
      }
      
      const result = await response.json();
      // Parse returns the file URL in the response
      const fileUrl = result.url || result.name;
      
      console.log('[FileUpload] File uploaded successfully:', fileUrl);
      return fileUrl;
    } catch (error) {
      console.error('[FileUpload] Error uploading to Back4App:', error);
      console.error('[FileUpload] Error details:', {
        message: error.message,
        error: error
      });
      throw error;
    }
  };

  const handleFileSelect = async (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;

    const newUrls = [];
    setDuplicateError(null);

    for (const file of files) {
      const fileId = `file_${Date.now()}_${fileIdCounter.current++}`;
      let workingFile = file;

      // Convert HEIC/HEIF to JPEG before compression/upload
      if (!file.type.startsWith('video/') && isHeicFile(file)) {
        workingFile = await convertHeicToJpeg(file);
      }

      const isVideo = workingFile.type.startsWith('video/');
      
      // Create local preview from processed file
      const previewUrl = URL.createObjectURL(workingFile);
      
      // Initialize file state (using functional update)
      setFileStates(prev => ({
        ...prev,
        [fileId]: {
          previewUrl,
          uploadedUrl: null,
          isUploading: true,
          isVideo: isVideo, // Store video flag for later use
        }
      }));

      try {
        // Compress file (images and videos)
        let processedFile;
        if (isVideo) {
          processedFile = await compressVideo(workingFile);
        } else {
          processedFile = await compressImage(workingFile);
        }
        
        // Reject a photo already submitted for a different unit, before it is
        // uploaded rather than after — which also means a rejected duplicate
        // costs the tech no upload time at all. Hash the compressed bytes,
        // since those are what get stored and therefore what the index holds.
        const shouldDedupe = !!dedupeContext && !isVideo;
        const primaryHash = shouldDedupe ? await hashFile(processedFile) : '';
        if (shouldDedupe && primaryHash) {
          const usage = await checkPhotoHash(primaryHash, dedupeContext, processedFile.size);
          if (usage) {
            setDuplicateError({ ...usage, fileName: file.name });
            setFileStates(prev => {
              const newState = { ...prev };
              delete newState[fileId];
              return newState;
            });
            URL.revokeObjectURL(previewUrl);
            continue;
          }
        }

        // Upload to Back4App
        let fileUrl;
        let uploadedFile = processedFile;
        try {
          fileUrl = await uploadToBack4App(processedFile, fileId);
        } catch (uploadErr) {
          console.warn('[FileUpload] Upload failed, retrying with extra compression...', uploadErr);
          // Extra-aggressive compression retry for images
          if (!isVideo) {
            const extraCompressed = await compressImage(processedFile, {
              maxSizeMB: 0.15,
              initialQuality: 0.45,
              maxWidthOrHeight: 1100,
            });
            fileUrl = await uploadToBack4App(extraCompressed, fileId);
            uploadedFile = extraCompressed;
          } else {
            // rethrow for videos
            throw uploadErr;
          }
        }

        if (shouldDedupe && primaryHash) {
          // The retry path re-compresses, so the bytes that landed differ from
          // the ones we hashed. Register both: the stored hash is what a
          // backfill would find, and the first-pass hash is what the same
          // source photo will produce next time it's picked.
          const storedHash = uploadedFile === processedFile
            ? primaryHash
            : await hashFile(uploadedFile);
          const meta = {
            url: fileUrl,
            fieldName: dedupeField,
            fieldLabel: dedupeField || label,
          };
          registerPhotoHash(storedHash, dedupeContext, { ...meta, size: uploadedFile.size });
          if (storedHash !== primaryHash) {
            registerPhotoHash(primaryHash, dedupeContext, { ...meta, size: processedFile.size });
          }
        }
        
        // Update file state with uploaded URL (using functional update)
        setFileStates(prev => ({
          ...prev,
          [fileId]: {
            ...prev[fileId],
            uploadedUrl: fileUrl,
            isUploading: false,
          }
        }));
        
        newUrls.push(fileUrl);
      } catch (error) {
        console.error('[FileUpload] Failed to upload file:', error);
        // Remove file state on error (using functional update)
        setFileStates(prev => {
          const newState = { ...prev };
          delete newState[fileId];
          return newState;
        });
        // Clean up preview URL
        URL.revokeObjectURL(previewUrl);
        // TODO: Show error toast/notification
      }
    }

    // Update parent with new URLs
    const updatedUrls = [...value, ...newUrls];
    onChange(updatedUrls);

    // Clear input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleRemove = (index) => {
    const urlToRemove = value[index];
    
    // Find and clean up file state
    const fileId = Object.keys(fileStates).find(id => {
      return fileStates[id].uploadedUrl === urlToRemove;
    });
    
    if (fileId) {
      const state = fileStates[fileId];
      // Clean up preview URL if it exists
      if (state.previewUrl) {
        URL.revokeObjectURL(state.previewUrl);
      }
      const newFileStates = { ...fileStates };
      delete newFileStates[fileId];
      setFileStates(newFileStates);
    }
    
    const updatedUrls = value.filter((_, i) => i !== index);
    onChange(updatedUrls);
  };

  // Get all files to display (both uploading and uploaded)
  const getAllFiles = () => {
    const files = [];
    
    // Add uploaded files
    value.forEach((url, index) => {
      const fileId = Object.keys(fileStates).find(id => {
        return fileStates[id].uploadedUrl === url;
      });
      
      if (fileId) {
        const state = fileStates[fileId];
        files.push({
          id: fileId,
          previewUrl: state.previewUrl || url,
          uploadedUrl: url,
          isUploading: state.isUploading || false,
          isUploaded: true,
          isVideo: state.isVideo || false,
          index,
        });
      } else {
        // File uploaded before component state was initialized
        // Try to detect if it's a video from URL
        const isVideo = url && (
          url.includes('video') || 
          url.match(/\.(mp4|mov|avi|webm)$/i)
        );
        files.push({
          id: `uploaded_${index}`,
          previewUrl: url,
          uploadedUrl: url,
          isUploading: false,
          isUploaded: true,
          isVideo: isVideo,
          index,
        });
      }
    });
    
    // Add files that are still uploading (not yet in value array)
    Object.entries(fileStates).forEach(([fileId, state]) => {
      if (!state.uploadedUrl && state.isUploading) {
        files.push({
          id: fileId,
          previewUrl: state.previewUrl,
          uploadedUrl: null,
          isUploading: true,
          isUploaded: false,
          isVideo: state.isVideo || false,
          index: null,
        });
      }
    });
    
    return files;
  };

  return (
    <div style={{ marginBottom: label ? '2.5rem' : '1rem' }}>
      {label && (
        <label
          style={{
            display: 'block',
            fontSize: '1rem',
            fontWeight: 500,
            color: '#374151',
            marginBottom: '0.75rem',
          }}
        >
          {label} {required && <span style={{ color: '#ef4444' }}>*</span>}
        </label>
      )}

      {/* File Input */}
      <div style={{ position: 'relative', marginBottom: '1rem' }}>
        <input
          ref={fileInputRef}
          type="file"
          accept={allowVideo ? "image/*,video/*" : "image/*"}
          // Note: Removing 'capture' attribute to allow both camera and photo selection
          // On mobile devices, this will show both "Take Photo/Video" and "Choose from Library" options
          // Works on Safari (iPhone), Chrome (Android/Samsung), and desktop browsers
          multiple={multiple}
          onChange={handleFileSelect}
          style={{
            width: '100%',
            padding: '1rem',
            paddingRight: getAllFiles().length > 0 ? '5rem' : '1rem', // Make room for file count
            fontSize: '1rem',
            border: hasError ? '1px solid #ef4444' : '1px solid #d1d5db',
            borderRadius: '0.5rem',
            outline: 'none',
            transition: 'border-color 0.2s',
            backgroundColor: '#ffffff',
            cursor: 'pointer',
            color: 'transparent', // Hide the default "No file chosen" text
          }}
          onFocus={(e) => {
            e.target.style.borderColor = '#2563eb';
          }}
          onBlur={(e) => {
            e.target.style.borderColor = hasError ? '#ef4444' : '#d1d5db';
          }}
        />
        {/* Custom label overlay - shows "Take / Upload Photo" or file count */}
        <div
          style={{
            position: 'absolute',
            left: '1rem',
            top: '50%',
            transform: 'translateY(-50%)',
            fontSize: '1rem',
            color: '#2563eb',
            pointerEvents: 'none',
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem',
          }}
        >
          <span>Take / Upload Photo</span>
          {getAllFiles().length > 0 && (
            <span style={{ color: '#374151', fontWeight: 500 }}>
              ({getAllFiles().length} {getAllFiles().length === 1 ? 'file' : 'files'})
            </span>
          )}
        </div>
      </div>

      {/* Preview Grid */}
      {getAllFiles().length > 0 && (
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(100px, 1fr))',
            gap: '1rem',
            marginTop: '1rem',
          }}
        >
          {getAllFiles().map((file) => {
            return (
            <div
              key={file.id}
              style={{
                position: 'relative',
                aspectRatio: '1',
                borderRadius: '0.5rem',
                overflow: 'hidden',
                border: '1px solid #e5e7eb',
                backgroundColor: file.isVideo ? '#f3f4f6' : 'transparent',
              }}
            >
              {file.isVideo ? (
                <>
                  <video
                    src={file.previewUrl}
                    style={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover',
                      display: 'block',
                    }}
                    controls={false}
                    muted
                    playsInline
                    preload="metadata"
                    onLoadedMetadata={(e) => {
                      // Try to show first frame by seeking to 0.1s
                      try {
                        e.target.currentTime = 0.1;
                        // Hide fallback icon once frame is loaded
                        const fallbackIcon = e.target.parentElement.querySelector('[data-fallback-icon]');
                        if (fallbackIcon) {
                          fallbackIcon.style.display = 'none';
                        }
                      } catch (err) {
                        console.warn('[FileUpload] Could not seek video:', err);
                      }
                    }}
                    onSeeked={(e) => {
                      // Hide fallback icon once frame is shown
                      const fallbackIcon = e.target.parentElement.querySelector('[data-fallback-icon]');
                      if (fallbackIcon) {
                        fallbackIcon.style.display = 'none';
                      }
                    }}
                    onError={(e) => {
                      // If video fails to load, hide video element and show fallback icon
                      e.target.style.display = 'none';
                      const fallbackIcon = e.target.parentElement.querySelector('[data-fallback-icon]');
                      if (fallbackIcon) {
                        fallbackIcon.style.display = 'flex';
                      }
                    }}
                  />
                  {/* Fallback icon overlay - shows initially, hidden when video frame loads */}
                  <div
                    data-fallback-icon
                    style={{
                      position: 'absolute',
                      top: '50%',
                      left: '50%',
                      transform: 'translate(-50%, -50%)',
                      fontSize: '2.5rem',
                      color: '#9ca3af',
                      pointerEvents: 'none',
                      zIndex: 1,
                      backgroundColor: 'rgba(255, 255, 255, 0.8)',
                      borderRadius: '50%',
                      width: '3.5rem',
                      height: '3.5rem',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    🎥
                  </div>
                  {/* Play button overlay - always visible on videos */}
                  <div
                    style={{
                      position: 'absolute',
                      top: '50%',
                      left: '50%',
                      transform: 'translate(-50%, -50%)',
                      pointerEvents: 'none',
                      zIndex: 2,
                      color: 'rgba(255, 255, 255, 0.9)',
                      filter: 'drop-shadow(0 2px 4px rgba(0, 0, 0, 0.3))',
                    }}
                  >
                    <FaPlayCircle style={{ fontSize: '3rem' }} />
                  </div>
                </>
              ) : (
                <img
                  src={file.previewUrl}
                  alt={file.isUploaded ? `Upload ${file.index + 1}` : 'Uploading...'}
                  style={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover',
                    display: 'block',
                  }}
                />
              )}
              {file.isUploading && (
                <div
                  style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    backgroundColor: 'rgba(0, 0, 0, 0.5)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <div
                    style={{
                      width: '2rem',
                      height: '2rem',
                      border: '3px solid #ffffff',
                      borderTop: '3px solid transparent',
                      borderRadius: '50%',
                      animation: 'spin 1s linear infinite',
                    }}
                  />
                </div>
              )}
              {file.isUploaded && file.index !== null && (
                <button
                  type="button"
                  onClick={() => handleRemove(file.index)}
                  style={{
                    position: 'absolute',
                    top: '0.25rem',
                    right: '0.25rem',
                    width: '1.5rem',
                    height: '1.5rem',
                    minWidth: '1.5rem',
                    minHeight: '1.5rem',
                    borderRadius: '50%',
                    backgroundColor: 'rgba(0, 0, 0, 0.6)',
                    color: '#ffffff',
                    border: 'none',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '1rem',
                    fontWeight: 'bold',
                    padding: 0,
                    lineHeight: 1,
                    flexShrink: 0,
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.backgroundColor = 'rgba(239, 68, 68, 0.8)';
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.backgroundColor = 'rgba(0, 0, 0, 0.6)';
                  }}
                >
                  ×
                </button>
              )}
            </div>
            );
          })}
        </div>
      )}

      {duplicateError && (
        <div
          role="alert"
          style={{
            marginTop: '0.75rem',
            padding: '0.85rem 1rem',
            border: '1px solid #fecaca',
            borderRadius: '0.5rem',
            backgroundColor: '#fef2f2',
            color: '#991b1b',
            fontSize: '0.9rem',
            lineHeight: 1.5,
          }}
        >
          <strong style={{ display: 'block', marginBottom: '0.35rem' }}>
            This photo has already been used
          </strong>
          {duplicateMessage(duplicateError)}
          {duplicateError.link && (
            <a
              href={duplicateError.link}
              target="_blank"
              rel="noopener noreferrer"
              style={{
                display: 'inline-block',
                marginTop: '0.5rem',
                color: '#b91c1c',
                fontWeight: 600,
              }}
            >
              View unit {duplicateError.unitNumber || ''} →
            </a>
          )}
          <button
            type="button"
            onClick={() => setDuplicateError(null)}
            style={{
              display: 'block',
              marginTop: '0.6rem',
              padding: 0,
              border: 'none',
              background: 'none',
              color: '#7f1d1d',
              fontSize: '0.8rem',
              textDecoration: 'underline',
              cursor: 'pointer',
            }}
          >
            Dismiss
          </button>
        </div>
      )}

      {hasError && errorMessage && (
        <p
          style={{
            marginTop: '0.5rem',
            fontSize: '0.875rem',
            color: '#ef4444',
          }}
        >
          {errorMessage}
        </p>
      )}

      {/* Add CSS for spinner animation and hide default file input button */}
      <style>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
        /* Hide default file input button text */
        input[type="file"]::-webkit-file-upload-button {
          visibility: hidden;
          display: none;
          width: 0;
          height: 0;
        }
        input[type="file"]::file-selector-button {
          display: none;
          visibility: hidden;
          width: 0;
          height: 0;
        }
        /* Hide the "No file chosen" text */
        input[type="file"] {
          color: transparent;
        }
        input[type="file"]::before {
          content: none;
        }
      `}</style>
    </div>
  );
}

